import React, { useState, useEffect, useCallback, useMemo } from "react";

const ADMIN_EMAIL = "anbunathan.r@gmail.com";

export default function AdminPanel({ userEmail, send }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const isAdmin = userEmail && userEmail.toLowerCase() === ADMIN_EMAIL;

  const loadStats = useCallback(() => {
    if (!isAdmin) return;
    setLoading(true);
    send({ action: "admin_stats", user_email: userEmail });
    setTimeout(() => setLoading(false), 6000);
  }, [isAdmin, send, userEmail]);

  // Send request 2s after mount
  useEffect(() => {
    if (!isAdmin) return;
    const t = setTimeout(loadStats, 2000);
    return () => clearTimeout(t);
  }, [isAdmin, loadStats]);

  // Listen for WS response
  useEffect(() => {
    const handler = (e) => {
      if (e.detail && e.detail.data) {
        setData(e.detail.data);
        setLoading(false);
      }
    };
    window.addEventListener("admin_stats", handler);
    return () => window.removeEventListener("admin_stats", handler);
  }, []);

  const updateSub = (targetEmail, newType, resetUsage = false) => {
    setUpdating(p => ({ ...p, [targetEmail]: true }));
    send({
      action: "admin_update_subscription",
      user_email: userEmail,
      target_email: targetEmail,
      subscription_type: newType,
      reset_usage: resetUsage,
    });
    setTimeout(() => {
      setUpdating(p => ({ ...p, [targetEmail]: false }));
      loadStats();
    }, 2000);
  };

  const processed = useMemo(() => {
    if (!data || !data.subscribers) return [];
    let rows = [...data.subscribers];
    if (filter !== "all") rows = rows.filter(s => s.subscription_type === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(s => s.email.toLowerCase().includes(q) || (s.name || "").toLowerCase().includes(q));
    }
    return rows;
  }, [data, filter, search]);

  if (!isAdmin) return null;

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h2 style={{ color: "#e6edf3", margin: 0 }}>Admin - Subscriber Management</h2>
        {data && data.summary && (
          <span style={{ color: "#8b949e", fontSize: 13 }}>
            Total: <b>{data.summary.total}</b> | Pro: <b style={{ color: "#FF9900" }}>{data.summary.pro}</b> | Free: <b>{data.summary.free}</b>
          </span>
        )}
        <button style={S.refreshBtn} onClick={loadStats}>Refresh</button>
      </div>

      <div style={S.toolbar}>
        <input style={S.search} placeholder="Search by email or name..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display: "flex", gap: 6 }}>
          {[["all", "All"], ["pro", "Pro"], ["free", "Free"]].map(([val, label]) => (
            <button key={val} style={{ ...S.filterBtn, ...(filter === val ? S.filterActive : {}) }} onClick={() => setFilter(val)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ color: "#8b949e", padding: 40, textAlign: "center" }}>Loading subscribers...</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr style={S.thead}>
                <th style={S.th}>Email</th>
                <th style={S.th}>Name</th>
                <th style={S.th}>Tier</th>
                <th style={S.th}>Projects Used</th>
                <th style={S.th}>Projects Left</th>
                <th style={S.th}>Joined</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {processed.length === 0 ? (
                <tr><td colSpan={7} style={{ color: "#8b949e", textAlign: "center", padding: 30 }}>No subscribers found</td></tr>
              ) : processed.map((s, i) => (
                <tr key={s.email} style={{ background: i % 2 === 0 ? "#0d1117" : "#161b22" }}>
                  <td style={S.td}><span style={{ color: "#58a6ff" }}>{s.email}</span></td>
                  <td style={S.td}>{s.name || "-"}</td>
                  <td style={S.td}>
                    <span style={{
                      padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: "bold",
                      background: s.subscription_type === "pro" ? "#FF990022" : "#21262d",
                      color: s.subscription_type === "pro" ? "#FF9900" : "#8b949e",
                      border: `1px solid ${s.subscription_type === "pro" ? "#FF9900" : "#30363d"}`,
                    }}>
                      {s.subscription_type.toUpperCase()}
                    </span>
                  </td>
                  <td style={S.td}>{s.usage.projects.used} / {s.usage.projects.limit}</td>
                  <td style={S.td}>
                    <span style={{ color: s.usage.projects.left === 0 ? "#f85149" : "#3fb950", fontWeight: "bold" }}>
                      {s.usage.projects.left}
                    </span>
                  </td>
                  <td style={S.td}>
                    <span style={{ color: "#8b949e", fontSize: 11 }}>
                      {s.created_at ? new Date(s.created_at * 1000).toLocaleDateString() : "-"}
                    </span>
                  </td>
                  <td style={S.td}>
                    <div style={{ display: "flex", gap: 4 }}>
                      {s.subscription_type === "free" ? (
                        <button style={S.btnUpgrade} disabled={updating[s.email]} onClick={() => updateSub(s.email, "pro")}>
                          {updating[s.email] ? "..." : "Upgrade"}
                        </button>
                      ) : (
                        <button style={S.btnDowngrade} disabled={updating[s.email]} onClick={() => updateSub(s.email, "free")}>
                          {updating[s.email] ? "..." : "Downgrade"}
                        </button>
                      )}
                      <button style={S.btnReset} disabled={updating[s.email]}
                        onClick={() => { if (window.confirm(`Reset usage for ${s.email}?`)) updateSub(s.email, s.subscription_type, true); }}>
                        Reset
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S = {
  wrap: { padding: "24px 32px", background: "#0d1117", minHeight: "70vh", borderRadius: 10, border: "1px solid #30363d" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 },
  refreshBtn: { padding: "6px 14px", background: "#21262d", border: "1px solid #30363d", borderRadius: 6, color: "#8b949e", cursor: "pointer", fontSize: 12 },
  toolbar: { display: "flex", gap: 12, marginBottom: 14, alignItems: "center", flexWrap: "wrap" },
  search: { flex: 1, minWidth: 200, padding: "7px 12px", background: "#161b22", border: "1px solid #30363d", borderRadius: 6, color: "#e6edf3", fontSize: 13, outline: "none" },
  filterBtn: { padding: "5px 12px", background: "#21262d", border: "1px solid #30363d", borderRadius: 6, color: "#8b949e", cursor: "pointer", fontSize: 12 },
  filterActive: { background: "#1f6feb22", borderColor: "#1f6feb", color: "#58a6ff" },
  tableWrap: { overflowX: "auto", borderRadius: 8, border: "1px solid #30363d" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  thead: { background: "#161b22" },
  th: { padding: "8px 12px", color: "#8b949e", fontWeight: 600, textAlign: "left", borderBottom: "1px solid #30363d", fontSize: 12 },
  td: { padding: "8px 12px", color: "#e6edf3", borderBottom: "1px solid #21262d" },
  btnUpgrade: { padding: "3px 10px", background: "#FF990022", border: "1px solid #FF9900", borderRadius: 5, color: "#FF9900", cursor: "pointer", fontSize: 11, fontWeight: "bold" },
  btnDowngrade: { padding: "3px 10px", background: "#21262d", border: "1px solid #30363d", borderRadius: 5, color: "#8b949e", cursor: "pointer", fontSize: 11 },
  btnReset: { padding: "3px 8px", background: "#1f6feb11", border: "1px solid #1f6feb44", borderRadius: 5, color: "#58a6ff", cursor: "pointer", fontSize: 11 },
};
